import { GroupStatusField } from '@Components/General/DynamicTable/Cells';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import Toolbar from '@Components/General/Toolbar';
import SavedFilters from '@Components/SavedFilters';
import Filters from '@Components/Specific/Filters/Filters';
import { contactFilters } from '@Components/Specific/Filters/filterSets';
import useCheckedItems from '@hooks/useCheckedItems';
import usePagination from '@hooks/usePagination';
import useSavedFilters from '@hooks/useSavedFilters';
import useSearchFilters from '@hooks/useSearchFilters';
import useSort from '@hooks/useSort';
import { Box, Typography } from '@mui/material';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLoaderDeps, useParams } from '@tanstack/react-router';
import {
    contactFiltersQueryOptions,
    contactsGroupItemsQueryOptions,
} from '@utils/queryOptions';
import ContactsTable from '@Views/Contacts/Components/ContactsTable';
import { useTranslation } from 'react-i18next';
import TableTabs, {
    TableTabProps,
} from '../../../../Components/General/table-tabs';
import { sum } from 'lodash';
import api from '@api/apiServices';
import { saveAsCSV } from '@utils/apiUtils';
import { closeOptions } from '@utils/utils';
import {
    GetAppRounded,
    HourglassDisabledRounded,
    HowToRegRounded,
    PlaylistRemoveRounded,
    RemoveDoneRounded,
} from '@mui/icons-material';
import { contactsExportFields } from '@data/exportFields';
import useDialog from '@hooks/useDialog';
import NoGroupContactsFound from './no-contacts-found';
import NoDataComponent from '@Components/General/Feedback/no-data-component';

const GroupContacts = ({ group, refetchGroup }) => {
    const { t } = useTranslation();
    const { groupId } = useParams({});
    const dialog = useDialog();

    const {
        data: { results: contacts, count },
        refetch: refetchContacts,
    } = useSuspenseQuery(
        contactsGroupItemsQueryOptions(groupId, useLoaderDeps({})),
    );

    const { page, setPage, pageCount, pageSize, setPageSize } = usePagination(
        count,
        'contacts',
    );

    const { getSort, setSort } = useSort();

    const { checkedItems, setCheckedItems, toggleItem } = useCheckedItems();

    const { data: savedFilters, refetch: refetchFilters } = useSuspenseQuery(
        contactFiltersQueryOptions(),
    );

    const savedFiltersHook = useSavedFilters({
        savedFilters,
        refetchFilters,
        mutationKey: 'contactFilter',
    });

    const {
        filters: filterParams,
        addFilter: addParam,
        hasFilter,
        exportFilters,
        removeFilter: removeParam,
        setFilter,
    } = useSearchFilters();

    const refetchData = () => {
        dialog.close();
        closeOptions();
        refetchGroup();
        refetchContacts();
    };

    const { mutate: updateStatus } = useMutation({
        mutationKey: ['batchUpdateGroupContactsItems'],
        onSuccess: refetchData,
    });

    const removeContactsMutation = useMutation({
        mutationKey: ['batchRemoveGroupContactsItems'],
        onSuccess: () => {
            refetchData();
            setCheckedItems([]);
        },
    });

    const exportFields = [
        ...contactsExportFields(),
        {
            label: t('Status'),
            value: 'contact_group_item.status',
        },
    ];

    const { refetch: exportContacts } = useQuery({
        enabled: false,
        queryKey: ['exportContactsGroupItems', groupId, exportFilters],
        queryFn: () =>
            api.contacts.groups[groupId].contacts
                .get({
                    ...exportFilters,
                    contact_id__in: checkedItems.join(','),
                })
                .then(res => {
                    saveAsCSV(res, {
                        name: 'contacts',
                        fields: exportFields,
                    });
                    closeOptions();
                    setCheckedItems([]);
                    return res;
                }),
    });

    const additonalFields = group?.todo
        ? [
              {
                  name: t('Status'),
                  field: 'contact_group_item.status',
                  sortField: 'contact_group_item__status',
                  Component: GroupStatusField,
                  updateStatus: ({ contactId, status }) =>
                      updateStatus({
                          groupId,
                          contact_ids: [contactId],
                          status,
                      }),
                  removeItem: contactId =>
                      removeContactsMutation.mutate({
                          groupId,
                          data: [contactId],
                      }),
              },
          ]
        : [];

    const contactsOptions: QuickOption[] = [
        {
            label: t('prompts:menu.removeContacts.', {
                count: checkedItems.length,
            }),
            onClick: () => {
                dialog.open({
                    title: t('prompts:title.removeContacts.', {
                        count: checkedItems.length,
                    }),
                    content: t('prompts:content.removeContacts'),
                    confirmMutation: removeContactsMutation,
                    onConfirm: () => ({ groupId, data: checkedItems }),
                    confirmLabel: t('Remove'),
                    confirmButtonProps: {
                        color: 'error',
                    },
                });
            },
            Icon: PlaylistRemoveRounded,
            disabled: !checkedItems.length,
        },
        {
            label: t('prompts:menu.exportContacts.', {
                count: checkedItems.length,
            }),
            onClick: exportContacts,
            Icon: GetAppRounded,
            disabled: !count,
        },
    ];

    const contactsTabs: TableTabProps[] = [
        {
            label: t('All'),
            value: '',
            color: 'default',
            count: sum(Object.values(group?.status_counts || {})),
            LinkComponent: Link,
            to: '.',
            search: search => ({
                ...search,
                contact_group_item__status: undefined,
            }),
        },
        {
            label: t('To Do'),
            value: '0',
            color: 'error',
            count: group?.status_counts?.['Todo'] || 0,
            LinkComponent: Link,
            to: '.',
            search: search => ({
                ...search,
                contact_group_item__status: ['0'],
            }),
        },
        {
            label: t('In Progress'),
            value: '1',
            color: 'warning',
            count: group?.status_counts?.['In Progress'] || 0,
            LinkComponent: Link,
            to: '.',
            search: search => ({
                ...search,
                contact_group_item__status: ['1'],
            }),
        },
        {
            label: t('Done'),
            value: '2',
            color: 'success',
            count: group?.status_counts?.['Done'] || 0,
            LinkComponent: Link,
            to: '.',
            search: search => ({
                ...search,
                contact_group_item__status: ['2'],
            }),
        },
    ];

    const status = filterParams?.contact_group_item__status?.[0] || '';

    const statusMap = {
        '0': 'Todo',
        '1': 'In Progress',
        '2': 'Done',
    };

    const currentStatusCount = group?.status_counts?.[statusMap[status]];

    const noDataMessages = {
        '2': {
            primary: t('prompts:noData.contactsGroup.done.primary'),
            secondary: t('prompts:noData.contactsGroup.done.secondary'),
            Icon: RemoveDoneRounded,
        },
        '1': {
            primary: t('prompts:noData.contactsGroup.in-progress.primary'),
            secondary: t('prompts:noData.contactsGroup.in-progress.secondary'),
            Icon: HourglassDisabledRounded,
        },
        '0': {
            primary: t('prompts:noData.contactsGroup.todo.primary'),
            secondary: t('prompts:noData.contactsGroup.todo.secondary'),
            Icon: HowToRegRounded,
        },
    };

    const noDataComponent =
        status && !currentStatusCount && group?.todo ? (
            <NoDataComponent {...noDataMessages[status]} />
        ) : (
            <NoGroupContactsFound />
        );

    return (
        <>
            <Toolbar>
                <Box overflow="auto" display="flex" gap={1}>
                    <SavedFilters {...savedFiltersHook} />
                    <Filters
                        filterSet={contactFilters}
                        filters={filterParams}
                        addFilter={addParam}
                        // addFilter={(k, v) => {
                        //     addParam(k, v);
                        //     closeOptions(true);
                        // }}
                        removeFilter={removeParam}
                        setFilter={setFilter}
                        hiddenFilters={['contact_group_item__status']}
                    />
                </Box>
                <QuickOptions options={contactsOptions} />
            </Toolbar>
            <ContactsTable
                tableHeader={
                    group?.todo ? (
                        <TableTabs value={status} tabs={contactsTabs} />
                    ) : (
                        <Typography>{`${count.toLocaleString()} ${t(
                            'Contacts',
                        )}`}</Typography>
                    )
                }
                noData={noDataComponent}
                contacts={contacts}
                pageSize={pageSize}
                setPageSize={setPageSize}
                count={count}
                checkedContacts={checkedItems}
                toggleCheckItem={toggleItem}
                setCheckedContacts={setCheckedItems}
                getSort={getSort}
                onSortChange={setSort}
                pageCount={pageCount}
                page={page}
                setPage={setPage}
                additionalColumns={additonalFields}
            />
        </>
    );
};

export default GroupContacts;
